<template>
  <div class="qv">
    <!-- Price and review wrapper -->
    <div class="qv__price-review">
      <ProductPrice
        :product="product"
        :selectedFirstOption="selectedFirstLevelOption"
      />
      <div v-if="product.id" class="qv__review-wrapper"> 
        <div class="yotpo-widget-instance" data-yotpo-instance-id="498880" :data-yotpo-product-id="product.id"></div>
      </div>
    </div>
    <!-- Product Images -->
    <QuickViewImages
      :product="product"
      :selectedColor="productForm.Color"
      :selectedTown="productForm.Town"
      :productForm="productForm"
      :firstOption="firstOption"
      :showWishlistIcon="showWishlistIcon"
    />

    <div v-if="product.featured_description_content">
      <template v-if="expandProductDetailsInPlace">
        <div class="qv__product-desc qv__product-desc--expand-in-place" :class="{ expanded: isDescriptionExpanded }">
          <span ref="descriptionRef" v-html="product.featured_description_content"></span>
          <span
            class="qv__product-desc-view-more"
            @click="toggleDescription"
            v-if="shouldShowToggle"
          >
            {{ isDescriptionExpanded ? 'Show Less' : 'View Product Details' }}
          </span>
        </div>
      </template>
      <template v-else-if="!hideProductDesc">
        <a :href="`/products/${product.handle}`" class="qv__product-desc">
          <span v-html="product.featured_description_content"></span>
          <span class="qv__product-desc-view-more">View Product Details</span>
        </a>
      </template>
    </div>

    <!-- Product Options -->
    <ProductOptions
      :product="product"
      :selectedOptions="productForm"
      :isSecondTurn="isSecondTurn"
      :hideColorOption="hideColorOption"
      :hideUnavailableOption="hideUnavailableOption"
      :isImageTypeSwatch="true"
      @on-option-change="onOptionChange"
    />

    <!-- Size Guide Btn -->
    <button v-if="enableSizeGuide && sizeGuideVal && !isSecondTurn" class="qv__size-guide" @click="toggleSizeDrawer">Size Guide</button>

    <!-- Product info bullets -->
    <div v-if="enableProductDetails" class="qv__product-info-bullets" v-html="renderProductInfoBullets?.content"></div>

    <!-- Product details accordion -->
    <Accordion v-if="enableProductDetails" :items="product?.product_details">
        <template v-slot:product_description>
          <div class="overview-wrapper">
            <p v-if="getOverviewDetails()" :key="index" class="section-detail-wrapper">
              <span class="section-detail-content" v-html="getOverviewDetails().description"></span>
            </p>
          </div>
        </template>
        <template v-slot:materials>
          <div class="materials-wrapper">
            <p v-if="getMaterialsDetails()" :key="index" class="section-detail-wrapper">
              <span class="section-detail-content" v-html="getMaterialsDetails().description"></span>
            </p>
          </div>
        </template>
        <template v-slot:features>
          <div class="details-wrapper">
            <p v-if="getProductDetailsContent()" :key="index" class="section-detail-wrapper">
              <span class="section-detail-content-list features" v-html="getProductDetailsContent().description"></span>
            </p>
          </div>
        </template>
    </Accordion>

    <!-- Product Stock Info -->
    <ProductStockInfo
      v-if="!hideStockInfo"
      :product="product"
      :selectedVariant="productForm.selectedVariant"
      :selectedFirstOption="selectedFirstLevelOption"
    />

    <!-- Add to cart btn -->
    <div v-if="!hideATC" data-stio-gtm-id="Quickview: ATC CTA">
      <ProductATC
        :product="product"
        :productForm="productForm"
        :selectedVariant="productForm.selectedVariant"
        :selectedFirstOption="selectedFirstLevelOption"
      />
    </div>

    <div v-if="!hideFreeShippingLabel" class="qv__free-shipping">Free shipping for orders over $99  |  Returns are easy and free</div>
    <a v-if="!hideATC" :href="productDetailsUrl" class="qv__details-link">View Full Product Details</a>
  </div>
</template>

<script setup>
  import { ref, computed, toRefs, onBeforeMount, reactive, onMounted, watch, nextTick } from 'vue';
  import { useStore } from "vuex";
  import ProductPrice from '../pdp-first-fold/components/product-price.vue';
  import ProductOptions from '../pdp-first-fold/components/product-options/index.vue';
  import ProductStockInfo from '../pdp-first-fold/components/product-stock-info.vue';
  import ProductATC from '../pdp-first-fold/components/product-atc.vue';
  import QuickViewImages from './quick-view-images.vue';
  import Accordion from '../global/accordion.vue';

  const props = defineProps({
    product: {
      type: Object,
      required: true,
    },

    selectedColor: {
      type: String,
      default: null,
    },

    hideProductDesc: {
      type: Boolean,
      default: false
    },

    hideColorOption: {
      type: Boolean,
      default: false
    },

    hideATC: {
      type: Boolean,
      default: false
    },

    hideFreeShippingLabel: {
      type: Boolean,
      default: false
    },

    hideUnavailableOption: {
      type: Boolean,
      default: false
    },

    layer: {
      type: String
    },

    subcategory: {
      type: String
    },

    onChangeOptionFromProductDetails: {
      type: Function,
    },

    selectedSize: {
      type: String,
      default: '',
    },

    selectedSeam: {
      type: String,
      default: '',
    },

    seamType: {
      type: String,
      default: ''
    },

    enableSizeGuide: {
      type: Boolean,
      default: false
    },

    enableProductDetails: {
      type: Boolean,
      default: false
    },

    hideStockInfo: {
      type: Boolean,
      default: false
    },

    showWishlistIcon : {
      type: Boolean,
      default: true
    },

    expandProductDetailsInPlace: {
      type: Boolean,
      default: false
    }
  })

  const { product,selectedColor,selectedSeam,seamType,selectedSize,layer,subcategory,enableSizeGuide } = toRefs(props)

  const store = useStore();

  const emit = defineEmits(['heading', 'component-updated'])
  const sizeGuideVal = ref(null)
  const sizeGuideFitType = ref('')
  const isDescriptionExpanded = ref(false);
  const shouldShowToggle = ref(false);
  const descriptionRef = ref(null);

  onBeforeMount(() => {
    emit('heading',product.value.title)
    initialSetup();
    window.refreshYotpoData(); 
  })

  onMounted(async () => {
    emit('component-updated');

    if (enableSizeGuide.value) {
      try {
        const sizeGuideData = await(await fetch(`${window.location.origin}/products/${product.value.handle}?view=size-guide-new`)).json();
        sizeGuideVal.value = sizeGuideData?.sizeguide || null;
        sizeGuideFitType.value = sizeGuideData?.fitType || '';
      } catch (error) {
        sizeGuideVal.value = null;
      }
    }

    checkDescriptionLines();
  })

  let productForm = reactive({
    selectedVariant: null,
  })

  let firstOption = reactive(product.value.options[0].name)

  const selectedFirstLevelOption = computed(() => {
    return store.state.pdp.firstLevelOptions?.[product.value.id];
  });

  const isSecondTurn = computed(() => {
    return product.value.tags.includes('Second Turn')
  })

  const additionalPropertyOptions = computed(() => {
    const additionalOpt = product.value?.additional_property_options;

    if (!additionalOpt) return [];

    if (typeof additionalOpt === 'string') {
      return additionalOpt.trim() ? additionalOpt.split(',') : [];
    }

    if (Array.isArray(additionalOpt)) {
      return additionalOpt.map(opt => opt.value);
    }

    return [];
  });

  const renderProductInfoBullets = computed(() => {
    return product.value?.product_info?.find((obj) => (obj.color === selectedColor.value));
  });

  const productDetailsUrl = computed(() => {
    const handle = product.value.handle;
    const selectedProductColor = productForm.Color || selectedColor.value;

    if (!selectedProductColor) {
      return `/products/${handle}`;
    }

    return `/products/${handle}?color=${encodeURIComponent(selectedProductColor)}`;
  });

  const renderProductDetails = computed(() => {
    const details = [];
    const sectionMap = {
      'material': 'Materials',
      'details': 'Details'
    }

    // Handle product overview
    if (product.value?.product_overview) {
      const overviewObject = {
        title: product.value?.product_overview?.title || 'Overview',
        key: 'Overview',
        detail: [
          {
            title: product.value?.product_overview?.heading || '',
            content: product.value?.product_overview?.content || ''
          }
        ]
      };
      details.push(overviewObject);
    }

    // Loop through product_info_ objects
    for (const [key, value] of Object.entries(product.value || {})) {
      if (key.startsWith('product_info_')) {
        // Check if the section_id matches the required section
        if (sectionMap[value.section_id]) {
          // Create a new object for this section
          const sectionObject = {
            title: sectionMap[value.section_id],
            key: sectionMap[value.section_id],
            detail: []
          };

          // Gather all keys starting with 'title_' and 'content_'
          const titleKeys = Object.keys(value).filter(k => k.startsWith('title_'));

          for (let i = 0; i <= titleKeys.length; i++) {
            const title = value[`title_${i}`] || '';
            const content = value[`content_${i}`] || '';

            if (title || content) {
              sectionObject.detail.push({
                title: title,
                content: content
              });
            }
          }

          details.push(sectionObject);
        }
      }
    }

    return details;
  });

  const toggleDescription = () => isDescriptionExpanded.value = !isDescriptionExpanded.value;

  const getOverviewDetails = () => {    
    return product.value?.product_details?.find((item) => item.key === "product_description") || {};
  }

  const getMaterialsDetails = () => {    
    return product.value?.product_details?.find((item) => item.key === "materials") || [];
  }

  const getProductDetailsContent = () => {    
    return product.value?.product_details?.find((item) => item.key === "features") || [];
  }

  const checkDescriptionLines = () => {
    nextTick(() => {
      const el = descriptionRef.value;
      if (!el) return;

      const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
      const height = el.offsetHeight;

      const numberOfLines = height / lineHeight;

      shouldShowToggle.value = numberOfLines > 2;
    });
  };

  const initialSetup = () => {
    const { options, variants } = product.value;

    if (isSecondTurn.value || options.length === 1) {
      productForm.selectedVariant = variants[0];
      options.forEach((option, index) => {
        productForm[option.name] = productForm.selectedVariant[`option${index + 1}`];
      })
    } else {
      if (selectedColor.value) {
        productForm.Color = selectedColor.value;
      } else {
        watchSelectedFirstLevelOptionOnceReady();
      }

      if (selectedSize.value) {
        productForm.Size = selectedSize.value;
      }

      if (selectedSeam.value) {
        productForm[seamType.value] = selectedSeam.value;
      }

      // For Inseam and Outseam 
      if (additionalPropertyOptions.value.length) {
        const inseamOutseamData = options.find(ele => ele.name === 'Inseam' || ele.name === 'Outseam')
        inseamOutseamData && (productForm[inseamOutseamData['name']] = inseamOutseamData['values'][0])
      }
    }
    updateVariantValue();
  }

  const onOptionChange = (key,value,index,length) => {
    productForm[key] = value;
    updateVariantValue();

    // if(key != 'Color' && key != 'Title'){
    //   updateColorOOS(value,index,length)
    // }

    if (props.onChangeOptionFromProductDetails) {
      props.onChangeOptionFromProductDetails(product.value, layer.value, subcategory.value, productForm);
    }
  }

  const updateVariantValue = () => {
    let variantValue = ''
    const {options} = product.value;
    options.forEach((option,index,array) => {
      variantValue +=  productForm[option.name];
      if (index != (array.length -1)) (variantValue += ' / ')
    })

    updateSelectedVariant(variantValue)
  }

  const updateSelectedVariant = (value) => {
    const variant = product.value.variants.find(item => item.value === value);
    if(variant) productForm.selectedVariant = variant;
    else productForm.selectedVariant = null;  
  }

  const toggleSizeDrawer = () => {
    window.toggleDrawer(true,'SizeGuideNew',
      {
        'product': product.value,
        'sizeguide': sizeGuideVal.value,
        'fitType': sizeGuideFitType.value,
        'heading': 'Size Guide'
      }
    )
  };

  const watchSelectedFirstLevelOptionOnceReady = () => {
    const unwatch = watch(selectedFirstLevelOption, (newVal) => {
      if (newVal?.value) {
        productForm[firstOption] = newVal.value;
        unwatch(); // Stop watching after the first execution
      }
    });
  };
</script>
